import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories, mainSiteUrl } from "@/mocks/categories";

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleScroll() {
      setScrolled(window.scrollY > 60);
    }
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-300/50 shadow-sm"
          : "bg-black/20 backdrop-blur-sm border-b border-white/10"
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link
            to="/"
            className={`font-heading text-xl md:text-2xl font-bold tracking-tight whitespace-nowrap cursor-pointer transition-colors ${
              scrolled ? "text-foreground-950" : "text-white drop-shadow-md"
            }`}
          >
            Itaobuy
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg whitespace-nowrap cursor-pointer transition-colors ${
                scrolled
                  ? "text-foreground-700 hover:text-foreground-950 hover:bg-background-100"
                  : "text-white/90 hover:text-white hover:bg-white/10"
              }`}
            >
              <i className="ri-home-4-line"></i>
              Home
            </Link>
            <a
              href="/#blog"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("blog")?.scrollIntoView({ behavior: "smooth" });
                window.history.replaceState(null, "", "/#blog");
              }}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap cursor-pointer transition-colors ${
                scrolled
                  ? "text-foreground-600 hover:text-foreground-950 hover:bg-background-100"
                  : "text-white/80 hover:text-white hover:bg-white/10"
              }`}
            >
              <i className="ri-book-open-line"></i>
              Shopping Guide
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className={`px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap cursor-pointer transition-colors ${
                scrolled
                  ? "text-foreground-600 hover:text-foreground-950 hover:bg-background-100"
                  : "text-white/80 hover:text-white hover:bg-white/10"
              }`}
            >
              Safety
            </a>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full border whitespace-nowrap cursor-pointer transition-colors ${
                  scrolled
                    ? "text-foreground-700 hover:text-foreground-950 border-secondary-200 hover:border-secondary-400 bg-background-50/80"
                    : "text-white/90 hover:text-white border-white/20 hover:border-white/40 bg-white/10"
                }`}
              >
                Categories
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-60 bg-background-50 rounded-xl border border-background-200/70 py-2 max-h-80 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className={`${cat.icon} text-base text-secondary-500`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="hidden md:flex items-center gap-2">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className={`px-5 py-2 text-sm font-semibold rounded-full whitespace-nowrap cursor-pointer transition-all duration-200 ${
                scrolled
                  ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                  : "bg-white text-primary-500 hover:bg-white/90"
              }`}
            >
              itaobuy sheet
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className={`px-5 py-2 text-sm font-semibold rounded-full whitespace-nowrap cursor-pointer transition-all duration-200 ${
                scrolled
                  ? "border-2 border-primary-500 text-primary-500 hover:bg-primary-500 hover:text-background-50"
                  : "border-2 border-white text-white hover:bg-white hover:text-primary-500"
              }`}
            >
              buy it now
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${
              scrolled ? "text-foreground-950" : "text-white"
            }`}
          >
            <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 px-4 py-4 flex flex-col gap-1 max-h-[80vh] overflow-y-auto">
          <Link
            to="/"
            onClick={() => setMobileMenuOpen(false)}
            className="flex items-center gap-2 px-4 py-3 text-sm font-semibold text-foreground-900 rounded-lg hover:bg-background-100 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-home-4-line text-accent-500"></i>
            Home
          </Link>
          <a
            href="/#blog"
            onClick={(e) => {
              e.preventDefault();
              setMobileMenuOpen(false);
              setTimeout(() => {
                document.getElementById("blog")?.scrollIntoView({ behavior: "smooth" });
                window.history.replaceState(null, "", "/#blog");
              }, 100);
            }}
            className="flex items-center gap-2 px-4 py-3 text-sm font-semibold text-foreground-900 rounded-lg hover:bg-background-100 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-book-open-line text-accent-500"></i>
            Shopping Guide
          </a>
          <Link
            to="/what-is-itaobuy-sheet"
            onClick={() => setMobileMenuOpen(false)}
            className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-foreground-700 rounded-lg hover:bg-background-100 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-guide-line text-secondary-500"></i>
            What Is Itaobuy Sheet
          </Link>
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-foreground-700 rounded-lg hover:bg-background-100 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-shield-check-line text-secondary-500"></i>
            Safety
          </a>
          <div className="border-t border-background-200/60 my-2"></div>
          <p className="px-4 py-1 text-xs font-extrabold text-accent-500 uppercase tracking-wider whitespace-nowrap flex items-center gap-1">
            <i className="ri-grid-line"></i> 14 Categories
          </p>
          <div className="grid grid-cols-2 gap-1">
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-2 px-3 py-2.5 text-xs text-foreground-700 rounded-lg hover:bg-background-100 hover:text-foreground-950 whitespace-nowrap cursor-pointer transition-colors"
              >
                <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className={`${cat.icon} text-accent-400 text-xs`}></i>
                </span>
                {cat.name}
              </a>
            ))}
          </div>
          <div className="border-t border-background-200/60 my-2"></div>
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="mx-2 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-full text-center hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            itaobuy sheet
          </a>
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="mx-2 py-3 text-sm font-semibold border-2 border-primary-500 text-primary-500 rounded-full text-center hover:bg-primary-500 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            buy it now
          </a>
        </div>
      )}
    </nav>
  );
}